import { getMerchantClient } from '@beatq/sdk-client';
import { mapErrorCode, readEntityFromEnvelope } from '@beatq/shared';
import {
  MERCHANT_PROGRAMME_SCREEN_COUNT,
  MERCHANT_SCREEN_IDS,
  TABLET_ALERT_SCREEN_IDS,
} from './catalog.js';

type ApiErr = { error?: { code: string; message: string } };

export type CoverageScreen = {
  app?: string;
  screenId?: string;
  implementationDepth?: string;
  status?: string;
};

export type CoverageDocument = {
  screens?: CoverageScreen[];
};

export type MerchantProgrammeAudit = {
  total: number;
  complete: number;
  missing: string[];
  tabletAlertScreens: number;
};

export type RealtimeConnectionTicket = {
  token?: string;
  url?: string;
  expiresAt?: string | null;
};

export type MerchantSettings = {
  notificationPreferences?: Record<string, boolean>;
  alertRouting?: Record<string, boolean>;
  timezone?: string | null;
  locale?: string | null;
};

export type TabletAlertPreferences = {
  orderBoardPush: boolean;
  kitchenDisplayPush: boolean;
  soundEnabled: boolean;
};

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export function auditMerchantProgrammeFromCoverage(
  coverage: CoverageDocument,
): MerchantProgrammeAudit {
  const merchantScreens = (coverage.screens ?? []).filter(
    (screen) => screen.app === 'merchant-console' && screen.screenId?.startsWith('SCR-MER-'),
  );
  const featureComplete = merchantScreens.filter(
    (screen) => screen.implementationDepth === 'feature',
  );
  const completeIds = new Set(featureComplete.map((screen) => screen.screenId));
  const missing = MERCHANT_SCREEN_IDS.filter((screenId) => !completeIds.has(screenId));

  return {
    total: MERCHANT_PROGRAMME_SCREEN_COUNT,
    complete: featureComplete.length,
    missing,
    tabletAlertScreens: TABLET_ALERT_SCREEN_IDS.length,
  };
}

export function isMerchantProgrammeComplete(audit: MerchantProgrammeAudit): boolean {
  return audit.complete >= MERCHANT_PROGRAMME_SCREEN_COUNT && audit.missing.length === 0;
}

export function connectionTicketFromEnvelope(
  envelope: unknown,
): RealtimeConnectionTicket | undefined {
  return readEntityFromEnvelope<RealtimeConnectionTicket>(envelope, ['ticket', 'connectionTicket']);
}

export function settingsFromEnvelope(envelope: unknown): MerchantSettings | undefined {
  return readEntityFromEnvelope<MerchantSettings>(envelope, ['settings']);
}

export function tabletAlertPreferencesFromSettings(
  settings: MerchantSettings | undefined,
): TabletAlertPreferences {
  const prefs = settings?.notificationPreferences ?? {};
  const routing = settings?.alertRouting ?? {};
  return {
    orderBoardPush: routing.orderBoard ?? prefs.push ?? true,
    kitchenDisplayPush: routing.kitchenDisplay ?? prefs.push ?? true,
    soundEnabled: routing.sound ?? prefs.sound ?? true,
  };
}

export async function createRealtimeConnectionTicket(body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/realtime/connection-tickets', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getMerchantSettings() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/settings');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateMerchantSettings(body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/settings', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}
